import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DialogBox } from "../../../../../components/Elements/DialogBox";
import {
  createDepartment,
  clearDepartmentStatus,
} from "../../../../../redux/OrganizationSetup/organizationSetupSlice";
import PopupCard from "../../../../Auth/PopupCard";
import { createPortal } from "react-dom";

const departmentOptions = [
  "Human Resources (HR)",
  "Finance",
  "Information Technology (IT)",
  "Sales",
  "Marketing",
  "Operations",
  "Procurement",
  "Legal",
  "Administration",
  "Research & Development (R&D)",
  "Customer Support",
  "Quality Assurance (QA)",
  "Production",
  "Logistics",
  "Health, Safety & Environment (HSE)",
  "Other",
];

const AddDepartment = ({ onOpen, onClose }) => {
  const dispatch = useDispatch();
  const {
    loading,
    error,
    departmentSuccess: success,
    departmentList = [],
  } = useSelector((state) => state.orgSetup);

  const [formData, setFormData] = useState({
    isActive: "true",
    otherDepartment: "",
  });
  const [selectedDepartments, setSelectedDepartments] = useState([]);

  const normalizeDepartmentName = (value = "") =>
    String(value).trim().toLowerCase();

  const existingDepartmentNames = new Set(
    departmentList
      .map((department) => normalizeDepartmentName(department?.name))
      .filter(Boolean),
  );

  useEffect(() => {
    if (!departmentList.length) return;

    const preselected = departmentList
      .map((department) => department?.name)
      .filter(Boolean)
      .filter((name) => departmentOptions.includes(name));

    setSelectedDepartments((prev) => {
      const next = new Set(prev);
      preselected.forEach((name) => next.add(name));
      return Array.from(next);
    });
  }, [departmentList]);

  // Reset & close after success
  const handleSuccessCleanup = () => {
    setFormData({ isActive: "true", otherDepartment: "" });
    setSelectedDepartments([]);
    dispatch(clearDepartmentStatus());
    onClose();
  };

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        handleSuccessCleanup();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleDepartment = (departmentName) => {
    setSelectedDepartments((prev) =>
      prev.includes(departmentName)
        ? prev.filter((n) => n !== departmentName)
        : [...prev, departmentName],
    );
  };

  const handleSave = () => {
    const newDepartmentNames = selectedDepartments.filter((departmentName) => {
      const actualName =
        departmentName === "Other"
          ? (formData.otherDepartment || "Other").trim()
          : departmentName;

      return !existingDepartmentNames.has(normalizeDepartmentName(actualName));
    });

    if (!newDepartmentNames.length) return;

    const departments = newDepartmentNames.map((departmentName) => ({
      name:
        departmentName === "Other"
          ? (formData.otherDepartment || "Other").trim()
          : departmentName,
      isActive: formData.isActive === "true",
    }));

    dispatch(createDepartment({ departments }));
  };

  const handleCancel = () => {
    setFormData({ isActive: "true", otherDepartment: "" });
    setSelectedDepartments([]);
    dispatch(clearDepartmentStatus());
    onClose();
  };

  const isOtherSelected = selectedDepartments.includes("Other");
  const newDepartmentNames = selectedDepartments.filter((departmentName) => {
    const actualName =
      departmentName === "Other"
        ? (formData.otherDepartment || "Other").trim()
        : departmentName;

    return !existingDepartmentNames.has(normalizeDepartmentName(actualName));
  });

  const isSaveDisabled =
    loading ||
    !newDepartmentNames.length ||
    (isOtherSelected &&
      !formData.otherDepartment.trim() &&
      newDepartmentNames.includes("Other"));

  return (
    <div>
      <DialogBox
        open={onOpen}
        onCancel={handleCancel}
        width="90%"
        maxWidth="700px"
      >
        <div
          className="flex flex-col"
          style={{ fontFamily: "'Lexend', sans-serif" }}
        >
          {/* ── Modal Header ── */}
          <div
            style={{
              // background: "linear-gradient(135deg, #06513D 0%, #0a7a5c 100%)",
              padding: "20px 24px 16px",
              borderRadius: "8px 8px 0 0",
            }}
          >
            <h2
              style={{
                color: "#06513D",
                fontSize: 18,
                fontWeight: 700,
                margin: 0,
              }}
            >
              Add Department(s)
            </h2>
            <p
              style={{
                color: "#06513D",
                fontSize: 13,
                marginTop: 4,
                marginBottom: 0,
              }}
            >
              Select one or more departments to add to your organisation
            </p>
          </div>

          {/* ── Body ── */}
          <div
            style={{
              padding: "20px 24px 24px",
              background: "#fff",
              borderRadius: "0 0 8px 8px",
            }}
          >
            {/* Selection counter */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 10,
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 600, color: "#4B5563" }}>
                Department Name
              </span>
              {selectedDepartments.length > 0 && (
                <span
                  style={{
                    background: "#E6EEEC",
                    color: "#06513D",
                    fontSize: 12,
                    fontWeight: 700,
                    padding: "2px 10px",
                    borderRadius: 20,
                    border: "1px solid #06513D40",
                  }}
                >
                  {selectedDepartments.length} selected
                </span>
              )}
            </div>

            {/* Checkbox grid */}
            <div
              style={{
                border: "1px solid #3BA2B959",
                borderRadius: 8,
                background: "#FAFAFA",
                padding: "12px 14px",
                maxHeight: 260,
                overflowY: "auto",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
                  gap: "6px 12px",
                }}
              >
                {departmentOptions.map((deptName) => {
                  const isChecked = selectedDepartments.includes(deptName);
                  const isOther = deptName === "Other";
                  return (
                    <label
                      key={deptName}
                      onClick={() => toggleDepartment(deptName)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "8px 10px",
                        borderRadius: 6,
                        cursor: "pointer",
                        border: isChecked
                          ? "1px solid #06513D"
                          : "1px solid transparent",
                        background: isChecked ? "#E6EEEC" : "transparent",
                        transition: "all 0.15s ease",
                        userSelect: "none",
                        fontStyle: isOther ? "italic" : "normal",
                      }}
                    >
                      {/* Custom checkbox */}
                      <span
                        style={{
                          width: 16,
                          height: 16,
                          borderRadius: 4,
                          border: isChecked ? "none" : "1.5px solid #9CA3AF",
                          background: isChecked ? "#06513D" : "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          transition: "all 0.15s ease",
                        }}
                      >
                        {isChecked && (
                          <svg
                            width="10"
                            height="8"
                            viewBox="0 0 10 8"
                            fill="none"
                          >
                            <path
                              d="M1 4L3.5 6.5L9 1"
                              stroke="#fff"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </span>
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: isChecked ? 600 : 400,
                          color: isChecked ? "#06513D" : "#374151",
                          lineHeight: 1.4,
                        }}
                      >
                        {deptName}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Other department text input */}
            {isOtherSelected && (
              <div style={{ marginTop: 12 }}>
                <label
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#06513D",
                    display: "block",
                    marginBottom: 4,
                  }}
                >
                  Specify Department Name{" "}
                  <span style={{ color: "#EF4444" }}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.otherDepartment}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      otherDepartment: e.target.value,
                    }))
                  }
                  placeholder="Enter custom department name…"
                  style={{
                    width: "100%",
                    border: "1px solid #3BA2B9",
                    borderRadius: 6,
                    padding: "8px 12px",
                    fontSize: 13,
                    color: "#111827",
                    outline: "none",
                    boxSizing: "border-box",
                    background: "#fff",
                  }}
                />
              </div>
            )}

            {/* Status row */}
            <div
              style={{
                marginTop: 16,
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <label
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#4B5563",
                  whiteSpace: "nowrap",
                }}
              >
                Status
              </label>
              <div style={{ display: "flex", gap: 6 }}>
                {["true", "false"].map((val) => {
                  const isActive = val === "true";
                  const selected = formData.isActive === val;
                  return (
                    <button
                      key={val}
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, isActive: val }))
                      }
                      style={{
                        padding: "5px 16px",
                        borderRadius: 20,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: "pointer",
                        border: selected
                          ? isActive
                            ? "1.5px solid #06513D"
                            : "1.5px solid #EF4444"
                          : "1.5px solid #D1D5DB",
                        background: selected
                          ? isActive
                            ? "#E6EEEC"
                            : "#FEE2E2"
                          : "#F9FAFB",
                        color: selected
                          ? isActive
                            ? "#06513D"
                            : "#DC2626"
                          : "#9CA3AF",
                        transition: "all 0.15s ease",
                      }}
                    >
                      {isActive ? "Active" : "Inactive"}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Action buttons */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
                marginTop: 24,
                borderTop: "1px solid #F3F4F6",
                paddingTop: 16,
              }}
            >
              <button
                onClick={handleCancel}
                disabled={loading}
                style={{
                  padding: "9px 22px",
                  border: "1.5px solid #06513D",
                  borderRadius: 6,
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#06513D",
                  background: "#fff",
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.6 : 1,
                  transition: "all 0.15s ease",
                }}
              >
                Cancel
              </button>

              <button
                onClick={handleSave}
                disabled={isSaveDisabled}
                style={{
                  padding: "9px 22px",
                  border: "none",
                  borderRadius: 6,
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#fff",
                  background: isSaveDisabled
                    ? "#9CA3AF"
                    : "linear-gradient(135deg, #06513D 0%, #0a7a5c 100%)",
                  cursor: isSaveDisabled ? "not-allowed" : "pointer",
                  opacity: isSaveDisabled ? 0.75 : 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  transition: "all 0.15s ease",
                  boxShadow: isSaveDisabled
                    ? "none"
                    : "0 2px 8px rgba(6,81,61,0.3)",
                }}
              >
                {loading ? (
                  <>
                    <span
                      style={{
                        width: 14,
                        height: 14,
                        border: "2px solid rgba(255,255,255,0.4)",
                        borderTopColor: "#fff",
                        borderRadius: "50%",
                        display: "inline-block",
                        animation: "spin 0.7s linear infinite",
                      }}
                    />
                    Saving…
                  </>
                ) : (
                  "Add Department"
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Spin keyframes */}
        <style>{`
          @keyframes spin { to { transform: rotate(360deg); } }
        `}</style>
      </DialogBox>

      {/* Error popup */}
      {error &&
        createPortal(
          <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black bg-opacity-40 px-4">
            <PopupCard
              type="error"
              title="Error"
              message={error}
              confirmText="Close"
              onConfirm={() => dispatch(clearDepartmentStatus())}
            />
          </div>,
          document.body,
        )}

      {/* Success popup */}
      {success &&
        createPortal(
          <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black bg-opacity-40 px-4">
            <PopupCard
              type="success"
              title="Success"
              message="Department created successfully!"
            />
          </div>,
          document.body,
        )}
    </div>
  );
};

export default AddDepartment;
